import { type UseQueryResult, useQuery } from '@tanstack/react-query';

import { fieldMappingsService } from '@/services/control/field-mappings-service';
import type {
  FieldMapping,
  FieldMappingFilter,
} from '@/types/control/field-mapping';

import { adminQueryKeys } from '@/hooks/admin/query-keys';
import { retryControlApi } from '@/hooks/admin/retry';

/**
 * A tenant's field mappings, filtered server-side.
 *
 * The filters are part of the query key, so changing one refetches rather than
 * narrowing a cached list on the client — which is the point of the ticket:
 * control-api ignored every filter until PR #1751, and the old page compensated
 * by filtering nothing at all.
 *
 * Disabled without a tenant id: `tenant_id` is required on this route, so
 * calling it early is a guaranteed `400`.
 */
export function useFieldMappings(
  tenantId: string,
  filter?: FieldMappingFilter
): UseQueryResult<FieldMapping[], Error> {
  return useQuery<FieldMapping[], Error>({
    queryKey: adminQueryKeys.fieldMappings(tenantId, filter),
    queryFn: ({ signal }) =>
      fieldMappingsService.list(tenantId, filter, signal),
    enabled: Boolean(tenantId),
    retry: retryControlApi,
  });
}
